import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import shortValues from '../utils/shortValues';
import '../../scss/index.scss';

const ScatterPlot = ({
    data = [],
    fullData = [],
    filteredTableSchedules = new Set(),
    onPointClick,
    selectedMDRM,
    onAreaSelect,
    selectMode,
    setSelectMode,
    hiddenSchedules,
    setHiddenSchedules,
    onResetSelection,
}) => {
    const svgRef = useRef();
    const tooltipRef = useRef();
    const brushRef = useRef();

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const bounds = svgRef.current.getBoundingClientRect();
        const { width, height } = bounds;

        const margin = {
            top: 20,
            right: 20,
            bottom: 60,
            left: 60,
        };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;

        const plotArea = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        const formattedData = data
            .map((d) => ({
                mdrm: d.data.MDRM,
                itemName: d.data.ItemName,
                schedule: d.data.Schedule,
                absoluteChangePercent: d.data.AbsoluteChangePercent,
                color: d.data.Color,
                value: d.data.E_value,
            }))
            .filter(
                (d) =>
                    typeof d.absoluteChangePercent === 'number' &&
                    typeof d.value === 'number' &&
                    d.value > 0,
            );

        const exponent = 0.3;
        const symmetricPow = (x) => Math.sign(x) * Math.abs(x) ** exponent;
        const inverseSymmetricPow = (y) => Math.sign(y) * Math.abs(y) ** (1 / exponent);

        const rawXExtent = d3.extent(formattedData, (d) => d.absoluteChangePercent);
        const x = d3
            .scaleLinear()
            .domain([symmetricPow(rawXExtent[0]), symmetricPow(rawXExtent[1])])
            .range([0, plotWidth]);

        const maxTickPower = Math.ceil(
            Math.log10(Math.max(Math.abs(rawXExtent[0]), Math.abs(rawXExtent[1]))),
        );
        const rawXTicks = d3
            .range(-maxTickPower, maxTickPower + 1)
            .flatMap((p) => [-(10 ** p), 10 ** p])
            .concat(0);

        let lastTickX = -Infinity;
        const minSpacing = 40;
        const filteredTicks = rawXTicks
            .filter((v) => v >= rawXExtent[0] && v <= rawXExtent[1])
            .map(symmetricPow)
            .filter((d) => {
                const px = x(d);
                if (px - lastTickX >= minSpacing) {
                    lastTickX = px;
                    return true;
                }
                return false;
            });

        const xAxis = d3
            .axisBottom(x)
            .tickValues(filteredTicks)
            .tickFormat((d) => shortValues(inverseSymmetricPow(d)));

        const yExtent = d3.extent(formattedData, (d) => d.value);
        const y = d3
            .scaleLog()
            .domain([Math.max(1e-2, yExtent[0]), yExtent[1]])
            .range([plotHeight, 0])
            .nice();

        const tickValues = y.ticks().filter((t) => Number.isInteger(Math.log10(t)));

        const yAxis = d3
            .axisLeft(y)
            .tickValues(tickValues)
            .tickFormat((d) => shortValues(d));

        plotArea
            .append('g')
            .attr('class', 'x-grid')
            .attr('transform', `translate(0,${plotHeight})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(filteredTicks)
                    .tickSize(-plotHeight)
                    .tickFormat(() => ''),
            )
            .selectAll('line')
            .attr('stroke', '#ccc')
            .attr('stroke-dasharray', '2,2');

        plotArea
            .append('g')
            .attr('class', 'y-grid')
            .call(
                d3
                    .axisLeft(y)
                    .ticks(6)
                    .tickSize(-plotWidth)
                    .tickFormat(() => ''),
            )
            .selectAll('line')
            .attr('stroke', '#ccc')
            .attr('stroke-dasharray', '2,2');

        plotArea
            .append('g')
            .attr('transform', `translate(0,${plotHeight})`)
            .call(xAxis)
            .selectAll('text')
            .attr('transform', 'rotate(-90)')
            .style('text-anchor', 'end')
            .attr('dx', '-0.8em')
            .attr('dy', '-0.5em');

        plotArea
            .append('text')
            .attr('x', plotWidth / 2)
            .attr('y', plotHeight + 50)
            .attr('fill', '#000')
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .text('Absolute Change Percent');

        plotArea
            .append('g')
            .call(yAxis)
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -plotHeight / 2)
            .attr('y', -40)
            .attr('fill', '#000')
            .style('text-anchor', 'middle')
            .text('Value');

        plotArea
            .append('line')
            .attr('x1', x(0))
            .attr('x2', x(0))
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#999')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3,3');

        plotArea
            .append('line')
            .attr('x1', 0)
            .attr('x2', plotWidth)
            .attr('y1', y(1))
            .attr('y2', y(1))
            .attr('stroke', '#999')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3,3');

        const tooltip = d3.select(tooltipRef.current);

        plotArea
            .selectAll('circle')
            .data(formattedData.filter((d) => !hiddenSchedules.has(d.schedule)))
            .enter()
            .append('circle')
            .attr('cx', (d) => x(symmetricPow(d.absoluteChangePercent)))
            .attr('cy', (d) => y(d.value))
            .attr('r', (d) => (selectedMDRM === d.mdrm ? 5 : 3))
            .style('fill', (d) =>
                selectedMDRM === d.mdrm ? '#d93954' : d.color || '#888',
            )
            .style('opacity', 0.85)
            .style('cursor', 'pointer')
            .on('click', (_, d) => onPointClick(d.mdrm))
            .on('mouseover', (event, d) => {
                const svgRect = svgRef.current.getBoundingClientRect();
                tooltip
                    .style('opacity', 1)
                    .html(
                        `
                    <strong>MDRM:</strong> ${d.mdrm}<br/>
                    <strong>Change:</strong> ${d.absoluteChangePercent}%<br/>
                    <strong>Item:</strong> ${d.itemName}<br/>
                    <strong>Schedule:</strong> ${d.schedule}<br/>
                    <strong>Value:</strong> ${d.value}
                `,
                    )
                    .style('left', `${event.clientX - svgRect.left + 10}px`)
                    .style('top', `${event.clientY - svgRect.top - 40}px`);
            })
            .on('mousemove', (event) => {
                const svgRect = svgRef.current.getBoundingClientRect();
                tooltip
                    .style('left', `${event.clientX - svgRect.left + 10}px`)
                    .style('top', `${event.clientY - svgRect.top - 40}px`);
            })
            .on('mouseout', () => tooltip.style('opacity', 0));

        if (selectMode) {
            let brushG;

            const brush = d3
                .brush()
                .extent([
                    [0, 0],
                    [plotWidth, plotHeight],
                ])
                .on('start brush end', (event) => {
                    const { selection } = event;
                    if (!selection || !brushG) return;

                    // 🔒 FIX: wait one frame so .selection actually exists
                    requestAnimationFrame(() => {
                        brushG
                            .select('.selection')
                            .attr('stroke', '#333')
                            .attr('stroke-width', 1)
                            .attr('fill', '#999')
                            .attr('fill-opacity', 0.2);
                    });

                    if (event.type === 'end') {
                        const [[x0, y0], [x1, y1]] = selection;
                        const area = {
                            x0: inverseSymmetricPow(x.invert(x0)),
                            x1: inverseSymmetricPow(x.invert(x1)),
                            y0: y.invert(y1),
                            y1: y.invert(y0),
                        };
                        onAreaSelect(area);
                    }
                });

            brushG = plotArea.append('g').attr('class', 'brush').call(brush);

            brushG.selectAll('.overlay').attr('cursor', 'crosshair');
        }
    }, [data, selectedMDRM, onPointClick, onAreaSelect, selectMode]);

    useEffect(() => {
        setHiddenSchedules(new Set());
    }, [data, setHiddenSchedules]);

    const allLegends = React.useMemo(() => {
        const map = new Map();
        fullData.forEach((d) => {
            const key = `${d.data.Schedule}-${d.data.Color}`;
            if (!map.has(key)) {
                map.set(key, {
                    schedule: d.data.Schedule,
                    color: d.data.Color,
                });
            }
        });
        return Array.from(map.values());
    }, [fullData]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div className="area-selection-header">
                <label htmlFor="enable-select-mode" className="checkbox-label">
                    <input
                        id="enable-select-mode"
                        type="checkbox"
                        checked={selectMode}
                        onChange={() => setSelectMode((prev) => !prev)}
                    />
                    Enable Area Selection
                </label>
                <button
                    type="button"
                    onClick={onResetSelection}
                    className="clear-all"
                >
                    Reset Selection
                </button>
            </div>

            <svg ref={svgRef} style={{ width: '100%', height: '500px' }} />

            <div
                ref={tooltipRef}
                className="scatterplot-tooltip"
                style={{
                    position: 'absolute',
                    opacity: 0,
                    color: '#fff',
                    pointerEvents: 'none',
                    background: '#333',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 10,
                }}
            />

            <div style={{ marginTop: '16px' }}>
                <strong style={{ fontSize: '13px', fontWeight: 'bold' }}>Legend:</strong>
                <div className="sp-legend-wrapper">
                    {allLegends.map(({ schedule, color }, idx) => {
                        const isUserHidden = hiddenSchedules.has(schedule);
                        const isInFilteredTable = filteredTableSchedules.has(schedule);

                        const isVisuallyDisabled = isUserHidden || !isInFilteredTable;

                        return (
                            <div
                                key={idx}
                                className="sp-schedules"
                                style={{
                                    cursor: 'pointer',
                                    opacity: isVisuallyDisabled ? 0.5 : 1,
                                    pointerEvents: 'auto',
                                    userSelect: 'none',
                                    filter: isVisuallyDisabled ? 'grayscale(1)' : 'none',
                                }}
                                onClick={() => {
                                    setHiddenSchedules((prev) => {
                                        const copy = new Set(prev);
                                        if (copy.has(schedule)) copy.delete(schedule);
                                        else copy.add(schedule);
                                        return copy;
                                    });
                                }}
                            >
                                <div
                                    className="sp-schedule"
                                    style={{
                                        backgroundColor: isUserHidden
                                            ? '#ccc'
                                            : color || '#888',
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: '11px',
                                        textDecoration: isVisuallyDisabled
                                            ? 'line-through'
                                            : 'none',
                                        color: isVisuallyDisabled ? '#999' : 'inherit',
                                    }}
                                >
                                    {schedule}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ScatterPlot;
